/*
 * ATC marquee — small scrolling trust strip that sits under the add to cart button.
 *
 * The strip is server-rendered with a fixed number of identical groups so it
 * animates with CSS alone. This component refines two things once it boots:
 *
 *   1. Duration is derived from the measured group width so the strip always
 *      travels at the configured pixels-per-second, no matter how long the
 *      copy is. Without this, markets with longer text (e.g. "Free Shipping
 *      from Netherlands") would visibly scroll faster than short ones.
 *   2. More groups are cloned in if the track is too narrow to cover the
 *      viewport twice, which would otherwise leave a visible gap mid-loop.
 */
class AtcMarquee extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.atc-marquee__viewport');
    this.track = this.querySelector('.atc-marquee__track');
    this.group = this.querySelector('.atc-marquee__group');

    if (!this.viewport || !this.track || !this.group) return;

    this.measure = this.measure.bind(this);
    this.measure();

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.group);
    } else {
      window.addEventListener('resize', this.measure);
    }

    // Web fonts land after first paint and change the group width.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.measure).catch(function () {});
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    window.removeEventListener('resize', this.measure);
  }

  measure() {
    const groupWidth = this.group.getBoundingClientRect().width;
    if (!groupWidth) return;

    this.topUpGroups(groupWidth);

    const speed = parseFloat(this.dataset.speed) || 45; // pixels per second
    this.style.setProperty('--atc-marquee-shift', '-' + groupWidth + 'px');
    this.style.setProperty('--atc-marquee-duration', (groupWidth / speed).toFixed(3) + 's');
    this.classList.add('atc-marquee--ready');
  }

  // Ensure the track is at least twice the visible width so the loop never
  // exposes empty space at the trailing edge.
  topUpGroups(groupWidth) {
    const needed = Math.ceil((this.viewport.getBoundingClientRect().width * 2) / groupWidth) + 1;
    let current = this.track.children.length;

    while (current < needed && current < 24) {
      const clone = this.group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      this.track.appendChild(clone);
      current += 1;
    }
  }
}

if (!customElements.get('atc-marquee')) {
  customElements.define('atc-marquee', AtcMarquee);
}
